import React from 'react';
import classNames from 'classnames';
import {
  TmoButtons as TmoButton,
  TmoH3,
  RawData,
  ConfirmationModalV2
} from '@tmobile/tmo-bps-syncup-web-component-lib';
import { ReactSVG } from 'react-svg';
import { useHistory } from 'react-router-dom';

import Feature from '../featureWrappers/Feature';
import IssueCommands from '../issueCommands/IssueCommands';
import CreateDeviceEvent from '../drive/CreateDeviceEvent';
import DeviceOnThing from './device/DeviceOnThing';
import DriveDevice from './device/DriveDevice';
import NoDeviceOnThing from './device/NoDeviceOnThing';
import { APP_MOBILE } from '../../utils/app_constants';

import style from './Thing.module.css';
import delete_icon from '../../static/assets/icons/action/delete.svg';
import privacy_icon from '../../static/assets/icons/privacy_status.svg';
import logs from '../../static/assets/icons/logs_icon.svg';

/**
 * DeviceDetailsSection Component
 *
 * ==========================================
 * COMPONENT HIERARCHY & RELATIONSHIPS
 * ==========================================
 *
 * PARENT COMPONENT (Who calls this):
 * └── Thing.jsx
 *     ├── Renders this component to show device information
 *     ├── Passes thing object, product type, and callback functions
 *     └── Part of refactoring to reduce Thing.jsx complexity (~70 lines extracted)
 *
 * CHILD COMPONENTS (Called by this component):
 * ├── Feature (featureWrappers/Feature.jsx)
 * │   └── Controls product-specific feature visibility
 * ├── IssueCommands (issueCommands/IssueCommands.jsx)
 * │   └── Sends commands to IoT devices (PETS, TRACKER, WATCH)
 * ├── CreateDeviceEvent (drive/CreateDeviceEvent.jsx)
 * │   └── Creates manual events for DRIVE devices
 * ├── DeviceOnThing (device/DeviceOnThing.jsx)
 * │   └── Displays IoT device details (PETS, TRACKER, WATCH)
 * ├── DriveDevice (device/DriveDevice.jsx)
 * │   └── Displays DRIVE device details (vehicles)
 * ├── NoDeviceOnThing (device/NoDeviceOnThing.jsx)
 * │   └── Shows device addition interface when no device present
 * └── TmoLibrary Components:
 *     ├── TmoButton - Action buttons (logs, privacy, delete, etc.)
 *     ├── TmoH3 - Section heading "DEVICE DETAILS"
 *     ├── RawData - Shows complete device object in modal
 *     └── ConfirmationModalV2 - Device deletion confirmation
 *
 * DATA FLOW:
 * Thing.jsx → DeviceDetailsSection → [Various device display components]
 *    ↓              ↓                            ↓
 *   Props      Device Actions              Device Information
 *                   ↓                            ↓
 *            Callbacks to Thing.jsx        User Interface
 *
 * ==========================================
 *
 * Displays and manages device information associated with a thing.
 * Handles different device configurations based on product types:
 *
 * Device Property Structure:
 * - Most products: thing.currentDevice (PETS, TRACKER, WATCH)
 * - DRIVE product: thing.device (legacy structure)
 * - No device: Shows "add device" interface
 *
 * Features:
 * - Raw device data viewer with modal display
 * - Product-specific action buttons (logs, privacy, commands, etc.)
 * - Device deletion with confirmation modal
 * - Device linking interface when no device present
 * - Different device display components based on product type
 *
 * @param {Object} thing - Thing object containing device information (from Thing.jsx)
 * @param {string} product - Product type constant (PETS, TRACKER, WATCH, DRIVE) (from Thing.jsx)
 * @param {Function} onAddDevice - Callback to handle device addition/linking (from Thing.jsx)
 * @param {Function} handleRemoveDeviceClicked - Callback to handle device deletion (from Thing.jsx)
 */
const DeviceDetailsSection = ({
  thing,
  product,
  onAddDevice,
  handleRemoveDeviceClicked
}) => {
  // Get router history for navigation actions
  const history = useHistory();

  return (
    <div className={style.column}>
      {/* ========== DEVICE DETAILS HEADER ========== */}
      <div className={style.heading_row}>
        {/* CHILD COMPONENT: TmoH3 - Section heading */}
        <TmoH3 className={style.heading} flush>
          DEVICE DETAILS
        </TmoH3>

        {/* Conditional rendering based on device presence */}
        {thing.currentDevice || thing.device ? (
          /* DEVICE PRESENT - Show device actions and information */
          <div className={classNames(style.row, style.action)}>
            {/* CHILD COMPONENT: RawData - Shows complete device object in modal */}
            <RawData
              rawObject={
                // Device property varies by product:
                // - Most products use 'currentDevice'
                // - DRIVE uses 'device' (legacy structure)
                thing.currentDevice ? thing.currentDevice : thing.device
              }
              title="Full Device Details"
              tooltip="Full Device Details"
              type=" "
              usePortal={false}
              modalType={'slide'}
              buttonType="magenta_primary"
            />

            {/* PETS-SPECIFIC: Hardware logs button */}
            {/* CHILD COMPONENT: TmoButton - Navigation to hardware logs */}
            {thing.thingType === 'PETS' && (
              <TmoButton
                onClick={() =>
                  history.push({
                    pathname: `devices/${thing.id}/hardware/${thing.currentDevice.id}/logs`,
                    state: { isThingTab: true } // Track navigation context
                  })
                }
                type="magenta_secondary"
                tooltip="Logs"
                icon={<ReactSVG src={logs} />}
              />
            )}

            {/* CHILD COMPONENT: Feature - Product-based feature gating */}
            <Feature
              isUserDetails // Only show in user detail views
              Products={[APP_MOBILE.PETS, APP_MOBILE.TRACKER, APP_MOBILE.WATCH]}
            >
              {/* CHILD COMPONENT: IssueCommands - Sends commands to IoT devices */}
              <IssueCommands
                isUserDetails
                deviceId={thing.currentDevice?.id} // Device identifier
                hardwareId={thing.currentDevice?.hardwareId} // Hardware identifier (IMEI, etc.)
              />
            </Feature>

            {/* DRIVE-SPECIFIC FEATURES */}
            {/* CHILD COMPONENT: Feature - DRIVE product feature gating */}
            <Feature isUserDetails Products={[APP_MOBILE.DRIVE]}>
              {/* Privacy Status - Only for real devices (not simulators) */}
              {/* CHILD COMPONENT: TmoButton - Navigation to privacy status */}
              {thing?.device?.simulatorType === 'NONE' && (
                <TmoButton
                  onClick={() =>
                    history.push(
                      `things/device/${thing.device.id}/privacy-status`
                    )
                  }
                  type="magenta_primary"
                  tooltip="Privacy Status"
                  icon={<ReactSVG src={privacy_icon} />}
                />
              )}

              {/* CHILD COMPONENT: CreateDeviceEvent - Manual event creation for DRIVE */}
              <CreateDeviceEvent
                deviceId={
                  thing.currentDevice ? thing.currentDevice.id : thing.device.id
                }
              />

              {/* Device Delete Button - Only for inactive DRIVE devices */}
              {/* CHILD COMPONENT: TmoButton - Device deletion trigger */}
              {thing.device?.status !== 'ACTIVE' && (
                <TmoButton
                  onClick={handleRemoveDeviceClicked} // Callback to Thing.jsx
                  type="magenta_secondary"
                  tooltip="Delete"
                  icon={<ReactSVG src={delete_icon} />}
                />
              )}
            </Feature>

            {/* DEVICE DELETE BUTTON - For IoT products (PETS, TRACKER, WATCH) */}
            {/* CHILD COMPONENT: Feature - IoT product feature gating */}
            <Feature
              isUserDetails
              Products={[APP_MOBILE.PETS, APP_MOBILE.TRACKER, APP_MOBILE.WATCH]}
            >
              {/* CHILD COMPONENT: TmoButton - Device deletion trigger */}
              <TmoButton
                onClick={handleRemoveDeviceClicked} // Callback to Thing.jsx
                type="magenta_secondary"
                tooltip="Delete"
                icon={<ReactSVG src={delete_icon} />}
              />
            </Feature>

            {/* CHILD COMPONENT: ConfirmationModalV2 - Device deletion confirmation */}
            <ConfirmationModalV2
              message={
                <div className={style.label}>
                  {'Are you sure you want to remove the device '}
                  <span className={style.value}>
                    {/* Display appropriate device ID based on product */}
                    {thing.currentDevice
                      ? thing.currentDevice.id
                      : thing.device.id}
                  </span>
                  ?
                </div>
              }
              title="Delete Device"
              acceptButtonText="Yes,Delete"
              type="danger" // Red styling for destructive action
              usePortal={false}
              confirmInput // Requires user to type confirmation
              modalType="delete"
            />
          </div>
        ) : (
          /* NO DEVICE PRESENT - Show device addition interface */
          /* CHILD COMPONENT: NoDeviceOnThing - Device addition interface */
          <NoDeviceOnThing
            thingType={thing.thingType}
            thingName={thing.name}
            onAddDevice={onAddDevice} // Callback to Thing.jsx → Things.jsx
            thingId={thing.id}
          />
        )}
      </div>

      {/* ========== DEVICE INFORMATION DISPLAY ========== */}
      {/* Different components render based on device type and product */}

      {thing?.currentDevice ? (
        /* CHILD COMPONENT: DeviceOnThing - IoT device display (PETS, TRACKER, WATCH) */
        <DeviceOnThing
          thingName={thing.name} // Context for display
          device={thing.currentDevice}
          type="primary_display" // Display mode configuration
        />
      ) : thing?.device ? (
        /* CHILD COMPONENT: DriveDevice - DRIVE device display (vehicles) */
        <DriveDevice
          thingName={thing.name}
          device={thing.device} // DRIVE uses 'device' property
        />
      ) : (
        /* NO DEVICE FALLBACK - Simple text message */
        <div className={style.text}>
          There is no device for the {thing.thingType} -
          <strong>{thing.name}</strong>
        </div>
      )}
    </div>
  );
};

/**
 * ==========================================
 * COMPONENT EXPORT & USAGE NOTES
 * ==========================================
 *
 * IMPORTED BY:
 * └── Thing.jsx
 *     └── Used to display device section after thing header
 *
 * COMPONENT RESPONSIBILITIES:
 * Device information display and management
 * Product-specific action buttons
 * Device addition/deletion workflows
 * Navigation to device-related pages
 *
 * PROPS FLOW:
 * Things.jsx → Thing.jsx → DeviceDetailsSection
 *     ↓           ↓              ↓
 *  User Data → Thing Data → Device Data
 *
 * CALLBACK FLOW:
 * DeviceDetailsSection → Thing.jsx → Things.jsx → API Services
 *         ↓                  ↓           ↓
 *   User Action    → Event Handler → API Call
 *
 */
export default DeviceDetailsSection;
